import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import type { JoinType } from '../types';

export interface JoinKeyPair {
  left: string;
  right: string;
}

export interface JoinBuilderModalProps {
  open: boolean;
  onClose: () => void;
  /** Available source table names. */
  sourceTables: string[];
  /** Available columns per table. */
  columnsByTable: Record<string, string[]>;
  /** Pre-filled values for editing existing join. */
  initialLeftTable?: string;
  initialRightTable?: string;
  initialJoinType?: JoinType;
  initialKeys?: JoinKeyPair[];
  /** Submit handler. */
  onSubmit: (data: {
    leftTable: string;
    rightTable: string;
    joinType: JoinType;
    keys: JoinKeyPair[];
  }) => void;
}

/**
 * Phase 4.3: Visual Join Builder Modal.
 * Allows users to configure joins between source tables with
 * left/right table selection, join type, and dynamic key pair rows.
 */
const JoinBuilderModal: React.FC<JoinBuilderModalProps> = ({
  open,
  onClose,
  sourceTables,
  columnsByTable,
  initialLeftTable = '',
  initialRightTable = '',
  initialJoinType = 'inner',
  initialKeys = [{ left: '', right: '' }],
  onSubmit,
}) => {
  const [leftTable, setLeftTable] = useState(initialLeftTable);
  const [rightTable, setRightTable] = useState(initialRightTable);
  const [joinType, setJoinType] = useState<JoinType>(initialJoinType);
  const [keys, setKeys] = useState<JoinKeyPair[]>(initialKeys.length > 0 ? initialKeys : [{ left: '', right: '' }]);

  const leftColumns = columnsByTable[leftTable] ?? [];
  const rightColumns = columnsByTable[rightTable] ?? [];

  const handleAddKey = () => setKeys([...keys, { left: '', right: '' }]);
  const handleRemoveKey = (idx: number) => setKeys(keys.filter((_, i) => i !== idx));
  const handleKeyChange = (idx: number, side: 'left' | 'right', value: string) => {
    const updated = [...keys];
    updated[idx] = { ...updated[idx], [side]: value };
    setKeys(updated);
  };

  const isValid = leftTable && rightTable && leftTable !== rightTable && keys.some(k => k.left && k.right);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 16 }}>
          Join Builder
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {/* Table Selection */}
          <Stack direction="row" spacing={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Left Table</InputLabel>
              <Select value={leftTable} label="Left Table" onChange={(e) => setLeftTable(e.target.value)}>
                {sourceTables.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Right Table</InputLabel>
              <Select value={rightTable} label="Right Table" onChange={(e) => setRightTable(e.target.value)}>
                {sourceTables.filter(t => t !== leftTable).map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Select>
            </FormControl>
          </Stack>

          {/* Join Type */}
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5, display: 'block' }}>
              Join Type
            </Typography>
            <ToggleButtonGroup
              value={joinType}
              exclusive
              onChange={(_, v) => { if (v) setJoinType(v); }}
              size="small"
              sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontSize: 12, px: 1.5 } }}
            >
              <ToggleButton value="inner">Inner</ToggleButton>
              <ToggleButton value="left">Left</ToggleButton>
              <ToggleButton value="right">Right</ToggleButton>
              <ToggleButton value="full">Full</ToggleButton>
              <ToggleButton value="cross">Cross</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Key Pairs */}
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                Join Keys
              </Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={handleAddKey} sx={{ fontSize: 11, textTransform: 'none' }}>
                Add Key
              </Button>
            </Stack>
            <Stack spacing={1}>
              {keys.map((pair, idx) => (
                <Stack key={idx} direction="row" spacing={1} alignItems="center">
                  <FormControl fullWidth size="small">
                    <Select
                      displayEmpty
                      value={pair.left}
                      onChange={(e) => handleKeyChange(idx, 'left', e.target.value)}
                      sx={{ fontSize: 12 }}
                    >
                      <MenuItem value="" disabled><em>Left column</em></MenuItem>
                      {leftColumns.map(c => <MenuItem key={c} value={c} sx={{ fontSize: 12 }}>{c}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <Typography sx={{ color: 'text.disabled', flexShrink: 0 }}>=</Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      displayEmpty
                      value={pair.right}
                      onChange={(e) => handleKeyChange(idx, 'right', e.target.value)}
                      sx={{ fontSize: 12 }}
                    >
                      <MenuItem value="" disabled><em>Right column</em></MenuItem>
                      {rightColumns.map(c => <MenuItem key={c} value={c} sx={{ fontSize: 12 }}>{c}</MenuItem>)}
                    </Select>
                  </FormControl>
                  {keys.length > 1 && (
                    <IconButton size="small" onClick={() => handleRemoveKey(idx)} sx={{ flexShrink: 0 }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Stack>
              ))}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} sx={{ fontSize: 12, textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!isValid}
          onClick={() => {
            onSubmit({ leftTable, rightTable, joinType, keys: keys.filter(k => k.left && k.right) });
            onClose();
          }}
          sx={{ fontSize: 12, textTransform: 'none' }}
        >
          Apply Join
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default JoinBuilderModal;
